import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { getFornitoreCalendarEvents } from "../../api/fornitoreCalendarApi";
import "../css/FornitoreCalendario.css";

const DAY_LABELS = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTH_LABELS = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

const FILTERS = [
  { value: "all", label: "Tutti" },
  { value: "pickup", label: "Ritiri" },
  { value: "delivery", label: "Consegne" },
  { value: "subscription_pickup", label: "Abbonamenti · Ritiri" },
  { value: "subscription_delivery", label: "Abbonamenti · Consegne" },
];

function pad(n) {
  return String(n).padStart(2, "0");
}

function toYmd(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
}

function parseYmd(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  return addDays(d, offset);
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function formatDateHuman(ymd) {
  const d = parseYmd(ymd);
  return d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
}

function parseEventDateTime(value) {
  if (!value) return { ymd: "", time: "" };

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      ymd: toYmd(value),
      time: `${pad(value.getHours())}:${pad(value.getMinutes())}`,
    };
  }

  const raw = String(value).trim();

  // Formati attesi dal backend: YYYY-MM-DDTHH:mm:ss oppure YYYY-MM-DD HH:mm:ss.
  // Non usiamo new Date come prima scelta, così evitiamo slittamenti timezone e date finite nel giorno sbagliato.
  const direct = raw.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}:\d{2}))?/);
  if (direct) {
    return { ymd: direct[1], time: direct[2] || "" };
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return {
      ymd: toYmd(parsed),
      time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
    };
  }

  return { ymd: "", time: "" };
}

function timeFromDateTime(value) {
  return parseEventDateTime(value).time || "--:--";
}

function dateFromDateTime(value) {
  return parseEventDateTime(value).ymd;
}

function normalizeEventType(type) {
  if (type === "pickup") return "Ritiro";
  if (type === "delivery") return "Consegna";
  if (type === "subscription_pickup") return "Abbonamento · Ritiro";
  if (type === "subscription_delivery") return "Abbonamento · Consegna";
  return "Evento";
}

function buildRange(cursor, viewMode) {
  if (viewMode === "month") {
    const monthStart = startOfMonth(cursor);
    const gridStart = startOfWeek(monthStart);
    const monthEnd = endOfMonth(cursor);
    const gridEnd = addDays(startOfWeek(addDays(monthEnd, 6)), 6);
    return { from: toYmd(gridStart), to: toYmd(gridEnd) };
  }

  const weekStart = startOfWeek(cursor);
  const weekEnd = addDays(weekStart, 6);
  return { from: toYmd(weekStart), to: toYmd(weekEnd) };
}

function groupEventsByDate(events) {
  const grouped = events.reduce((acc, event) => {
    const day = dateFromDateTime(event.start);
    if (!day) return acc;

    if (!acc[day]) acc[day] = [];
    acc[day].push(event);
    return acc;
  }, {});

  Object.keys(grouped).forEach((day) => {
    grouped[day].sort((a, b) =>
      `${timeFromDateTime(a.start)}-${a.title || ""}`.localeCompare(
        `${timeFromDateTime(b.start)}-${b.title || ""}`,
      ),
    );
  });

  return grouped;
}

function EventPill({ event, onClick }) {
  return (
    <button
      type="button"
      className={`fcal-event fcal-event--${event.type}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick(event);
      }}
    >
      <span className="fcal-event-time">
        {timeFromDateTime(event.start)}–{timeFromDateTime(event.end)}
      </span>
      <span className="fcal-event-title">{event.title}</span>
    </button>
  );
}

function EventDetail({ event, onClose }) {
  if (!event) return null;

  return (
    <div className="fcal-modal-backdrop" onClick={onClose} role="presentation">
      <div className="fcal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fcal-modal-head">
          <div>
            <span className={`fcal-type fcal-type--${event.type}`}>
              {normalizeEventType(event.type)}
            </span>
            <h3>{event.customer_name || "Cliente"}</h3>
          </div>

          <button type="button" className="fcal-icon-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="fcal-detail-grid">
          <div>
            <span>Quando</span>
            <strong>
              {formatDateHuman(dateFromDateTime(event.start))}
              <br />
              {timeFromDateTime(event.start)}–{timeFromDateTime(event.end)}
            </strong>
          </div>

          <div>
            <span>Stato</span>
            <strong>{event.status_label || event.status || "—"}</strong>
          </div>

          {event.plan_name ? (
            <div>
              <span>Piano</span>
              <strong>{event.plan_name}</strong>
            </div>
          ) : null}

          {event.customer_email ? (
            <div>
              <span>Email</span>
              <strong>{event.customer_email}</strong>
            </div>
          ) : null}

          {event.customer_phone ? (
            <div>
              <span>Telefono cliente</span>
              <strong>
                <a href={`tel:${event.customer_phone}`}>
                  {event.customer_phone}
                </a>
              </strong>
            </div>
          ) : null}

          {event.address_source_type === "organization" &&
          event.organization_name ? (
            <div className="fcal-detail-full">
              <span>Ritiro/consegna presso</span>
              <strong>{event.organization_name}</strong>
            </div>
          ) : null}

          <div className="fcal-detail-full">
            <span>
              {event.address_source_type === "organization"
                ? "Indirizzo organizzazione"
                : "Indirizzo"}
            </span>
            <strong>{event.address || "Indirizzo non disponibile"}</strong>
            {event.pickup_phone ? (
              <div>
                Telefono: <a href={`tel:${event.pickup_phone}`}>{event.pickup_phone}</a>
              </div>
            ) : null}
            {event.address_notes ? <div>Note: {event.address_notes}</div> : null}
            {event.maps_url ? (
              <a
                className="fcal-map-link"
                href={event.maps_url}
                target="_blank"
                rel="noreferrer"
              >
                Apri in Maps
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FornitoreCalendario() {
  const token = localStorage.getItem("token");
  const [cursor, setCursor] = useState(() => new Date());
  const [viewMode, setViewMode] = useState("week");
  const [filter, setFilter] = useState("all");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const range = useMemo(() => buildRange(cursor, viewMode), [cursor, viewMode]);

  const filteredEvents = useMemo(() => {
    if (filter === "all") return events;
    return events.filter((event) => event.type === filter);
  }, [events, filter]);

  const eventsByDate = useMemo(
    () => groupEventsByDate(filteredEvents),
    [filteredEvents],
  );

  const title = useMemo(() => {
    if (viewMode === "month") {
      return `${MONTH_LABELS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    }

    const weekStart = parseYmd(range.from);
    const weekEnd = parseYmd(range.to);
    return `${weekStart.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
    })} – ${weekEnd.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })}`;
  }, [cursor, range, viewMode]);

  async function loadCalendar() {
    if (!token) return;

    setLoading(true);
    try {
      const data = await getFornitoreCalendarEvents(token, range);
      setEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (err) {
      toast.error(err.message || "Errore caricamento calendario");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, range.from, range.to]);

  function goPrev() {
    setCursor((prev) => addDays(prev, viewMode === "month" ? -30 : -7));
  }

  function goNext() {
    setCursor((prev) => addDays(prev, viewMode === "month" ? 30 : 7));
  }

  function goToday() {
    setCursor(new Date());
  }

  const weekDays = useMemo(() => {
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, index) => addDays(start, index));
  }, [cursor]);

  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const gridStart = startOfWeek(monthStart);
    return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  }, [cursor]);

  const counters = useMemo(() => {
    return {
      all: events.length,
      pickup: events.filter((event) => event.type === "pickup").length,
      delivery: events.filter((event) => event.type === "delivery").length,
      subscription_pickup: events.filter(
        (event) => event.type === "subscription_pickup" || event.type === "subscription_delivery",
      ).length,
    };
  }, [events]);

  return (
    <main className="fcal-page">
      <section className="fcal-hero">
        <div>
          <p className="fcal-kicker">Calendario fornitore</p>
          <h1>Ritiri e consegne</h1>
          <p>
            Una vista semplice per vedere cosa ritirare, cosa consegnare e gli
            abbonamenti ricorrenti.
          </p>
        </div>

        <div className="fcal-summary">
          <div>
            <strong>{counters.pickup}</strong>
            <span>Ritiri</span>
          </div>
          <div>
            <strong>{counters.delivery}</strong>
            <span>Consegne</span>
          </div>
          <div>
            <strong>{counters.subscription_pickup}</strong>
            <span>Abbonamenti</span>
          </div>
        </div>
      </section>

      <section className="fcal-card">
        <div className="fcal-toolbar">
          <div className="fcal-nav">
            <button type="button" onClick={goPrev}>
              ‹
            </button>
            <button type="button" onClick={goToday}>
              Oggi
            </button>
            <button type="button" onClick={goNext}>
              ›
            </button>
          </div>

          <h2>{title}</h2>

          <div className="fcal-view-switch">
            <button
              type="button"
              className={viewMode === "week" ? "is-active" : ""}
              onClick={() => setViewMode("week")}
            >
              Settimana
            </button>
            <button
              type="button"
              className={viewMode === "month" ? "is-active" : ""}
              onClick={() => setViewMode("month")}
            >
              Mese
            </button>
          </div>
        </div>

        <div className="fcal-filters">
          {FILTERS.map((item) => (
            <button
              type="button"
              key={item.value}
              className={filter === item.value ? "is-active" : ""}
              onClick={() => setFilter(item.value)}
            >
              {item.label}
              <span>{counters[item.value] ?? counters.all}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="fcal-empty">Caricamento calendario...</div>
        ) : viewMode === "week" ? (
          <div className="fcal-week-grid">
            {weekDays.map((day) => {
              const ymd = toYmd(day);
              const dayEvents = eventsByDate[ymd] || [];
              const isToday = ymd === toYmd(new Date());

              return (
                <div
                  className={`fcal-day-col ${isToday ? "is-today" : ""}`}
                  key={ymd}
                >
                  <div className="fcal-day-head">
                    <span>{DAY_LABELS[day.getDay()]}</span>
                    <strong>{pad(day.getDate())}</strong>
                  </div>

                  <div className="fcal-day-events">
                    {dayEvents.length ? (
                      dayEvents.map((event) => (
                        <EventPill
                          key={event.id}
                          event={event}
                          onClick={setSelectedEvent}
                        />
                      ))
                    ) : (
                      <div className="fcal-no-events">Nessun evento</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="fcal-month-grid">
            {DAY_LABELS.slice(1)
              .concat(DAY_LABELS[0])
              .map((label) => (
                <div className="fcal-month-label" key={label}>
                  {label}
                </div>
              ))}

            {monthDays.map((day) => {
              const ymd = toYmd(day);
              const dayEvents = eventsByDate[ymd] || [];
              const inMonth = day.getMonth() === cursor.getMonth();
              const isToday = ymd === toYmd(new Date());

              return (
                <div
                  className={`fcal-month-day ${inMonth ? "" : "is-muted"} ${
                    isToday ? "is-today" : ""
                  }`}
                  key={ymd}
                >
                  <div className="fcal-month-num">{day.getDate()}</div>
                  <div className="fcal-month-events">
                    {dayEvents.slice(0, 3).map((event) => (
                      <EventPill
                        key={event.id}
                        event={event}
                        onClick={setSelectedEvent}
                      />
                    ))}
                    {dayEvents.length > 3 ? (
                      <button
                        type="button"
                        className="fcal-more"
                        onClick={() => setCursor(day)}
                      >
                        +{dayEvents.length - 3} altri
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <EventDetail
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </main>
  );
}
